<template>
  <div>
    <ts-page-title
      :title="$t('attendanceDetail.pageTitle')"
      :breadcrumb="[
        { text: $t('home'), href: '/' },
        {
          text: $t('attendanceDetail.pageTitle'),
          active: true,
        },
      ]"
    />
    <ts-panel>
      <ts-panel-wrapper>
        <div class="row">
          <div class="col-md-2 form-group ts-px-2">
            <label class="form-col-label control-label required ts-mb-2">{{
              $t('attendanceDetail.dateFrom')
            }}</label>
            <DatePicker
              class="ts-w-full"
              :value="dateFrom"
              @on-change="onChangeDateFrom"
              format="dd-MM-yyyy"
              type="date"
              :class="{
                'ivu-form-item-error': errors.has('date_from'),
              }"
            />
            <div
              class="ivu-form-item-error ts-text-red-600"
              v-if="errors.has('date_from')"
            >
              {{ errors.first('date_from') }}
            </div>
          </div>
          <div class="col-md-2 form-group ts-px-2">
            <label class="form-col-label control-label required ts-mb-2">{{
              $t('attendanceDetail.dateTo')
            }}</label>
            <DatePicker
              class="ts-w-full"
              :value="dateTo"
              @on-change="onChangeDateTo"
              format="dd-MM-yyyy"
              type="date"
              :class="{
                'ivu-form-item-error': errors.has('date_to'),
              }"
            />
            <div
              class="ivu-form-item-error ts-text-red-600"
              v-if="errors.has('date_to')"
            >
              {{ errors.first('date_to') }}
            </div>
          </div>
          <div class="col-md-2 form-group ts-px-2">
            <label class="form-col-label control-label required ts-mb-2">{{
              $t('attendanceDetail.organisationalStructure')
            }}</label>
            <Select
              v-model="org_chart_structure_id"
              clearable
              @on-change="employee_id = null"
              :class="{
                'ivu-form-item-error': errors.has('org_chart_structure_id'),
              }"
            >
              <Option
                v-for="(orgChartStructure, index) in orgChartStructures"
                :key="index"
                :value="orgChartStructure.org_chart_structure_id"
                >{{ orgChartStructure.org_chart_structure }}
              </Option>
            </Select>
            <div
              class="ivu-form-item-error ts-text-red-600"
              v-if="errors.has('org_chart_structure_id')"
            >
              {{ errors.first('org_chart_structure_id') }}
            </div>
          </div>
          <div class="col-md-2 form-group ts-px-2">
            <label class="form-col-label control-label ts-mb-2">{{
              $t('attendanceDetail.employeeName')
            }}</label>
            <Select
              v-model="employee_id"
              clearable
              multiple
              filterable
              placeholder="All"
              :loading="EmployeeLoading"
              :not-found-text="EmployeeLoading ? '' : 'No options available'"
            >
              <Option
                v-for="(employee, index) in employees"
                :key="index"
                :value="employee.employee_id"
                >{{ employee.employee_name_en }}
              </Option>
            </Select>
          </div>
          <div class="col-md-2 form-group ts-px-2 ts-pt-6">
            <Button
              :disabled="loading"
              :loading="loading"
              class="ts-mb-1"
              type="primary"
              @click="fetchData"
              >{{ $t('view') }}
            </Button>
            <Button
              class="ts-mb-1 ts-ml-3"
              type="success"
              :disabled="exporting"
              :loading="exporting"
              ghost
              @click="exportExcel"
            >
              <Icon type="ios-document-outline" />
              {{ $t('excel') }}
            </Button>
          </div>
        </div>
      </ts-panel-wrapper>
      <Table
        :columns="columns"
        :data="resources"
        size="small"
        :loading="loading"
        max-height="700"
        border
      >
        <template slot-scope="{ row }" slot="total_hour1">
          <strong>{{ row.total_hour1 }}</strong>
        </template>
        <template slot-scope="{ row }" slot="total_hour2">
          <strong>{{ row.total_hour2 }}</strong>
        </template>
        <template slot-scope="{ row }" slot="total_hour3">
          <strong>{{ row.total_hour3 }}</strong>
        </template>
        <template slot-scope="{ row }" slot="total_hour4">
          <strong>{{ row.total_hour4 }}</strong>
        </template>
      </Table>
      <div class="ts-flex ts-items-center ts-justify-end ts-p-4">
        <ts-i-pagination
          v-model="pagination"
          @navigate="fetchData"
        ></ts-i-pagination>
      </div>
    </ts-panel>
  </div>
</template>

<script>
import { Errors } from 'form-backend-validation'
import { mapActions, mapState } from 'vuex'
import moment from 'moment'
import * as FileDownload from 'downloadjs'
import { trim } from 'lodash'

export default {
  name: 'attendance-detail',
  components: {},
  data() {
    return {
      errors: new Errors(),
      loading: false,
      exporting: false,
      EmployeeLoading: false,
      dateFrom: moment().startOf('month').format('DD-MM-YYYY'),
      dateTo: moment().endOf('month').format('DD-MM-YYYY'),
      org_chart_structure_id: null,
      employee_id: null,
    }
  },
  computed: {
    ...mapState('report/attendanceDetail', [
      'resources',
      'pagination',
      'orgChartStructures',
      'employees',
    ]),
    columns() {
      return [
        {
          title: this.$t('attendanceDetail.cardNumber'),
          key: 'card_id',
          minWidth: 150,
        },
        {
          title: this.$t('attendanceDetail.nameKh'),
          key: 'employee_name_kh',
          minWidth: 150,
        },
        {
          title: this.$t('attendanceDetail.nameEn'),
          key: 'employee_name_en',
          minWidth: 150,
        },
        {
          title: this.$t('attendanceDetail.position'),
          key: 'position',
          minWidth: 250,
        },
        {
          title: this.$t('attendanceDetail.shift'),
          key: 'shift_name',
          minWidth: 150,
        },
        {
          title: this.$t('attendanceDetail.dateScan'),
          key: 'datescan',
          align: 'center',
          minWidth: 150,
        },
        {
          title: this.$t('attendanceDetail.t1'),
          align: 'center',
          children: [
            {
              title: this.$t('attendanceDetail.checkIn'),
              align: 'center',
              width: 100,
              key: 't1',
            },
            {
              title: this.$t('attendanceDetail.checkOut'),
              align: 'center',
              width: 100,
              key: 't2',
            },
          ],
        },
        {
          title: this.$t('attendanceDetail.total1'),
          slot: 'total_hour1',
          width: 100,
          align: 'center',
        },
        {
          title: this.$t('attendanceDetail.t2'),
          align: 'center',
          children: [
            {
              title: this.$t('attendanceDetail.checkIn'),
              align: 'center',
              width: 100,
              key: 't3',
            },
            {
              title: this.$t('attendanceDetail.checkOut'),
              align: 'center',
              width: 100,
              key: 't4',
            },
          ],
        },
        {
          title: this.$t('attendanceDetail.total2'),
          slot: 'total_hour2',
          width: 100,
          align: 'center',
        },
        {
          title: this.$t('attendanceDetail.t3'),
          align: 'center',
          children: [
            {
              title: this.$t('attendanceDetail.checkIn'),
              align: 'center',
              width: 100,
              key: 't5',
            },
            {
              title: this.$t('attendanceDetail.checkOut'),
              align: 'center',
              width: 100,
              key: 't6',
            },
          ],
        },
        {
          title: this.$t('attendanceDetail.total3'),
          slot: 'total_hour3',
          width: 100,
          align: 'center',
        },
        {
          title: this.$t('attendanceDetail.t4'),
          align: 'center',
          children: [
            {
              title: this.$t('attendanceDetail.checkIn'),
              align: 'center',
              width: 100,
              key: 't7',
            },
            {
              title: this.$t('attendanceDetail.checkOut'),
              align: 'center',
              width: 100,
              key: 't8',
            },
          ],
        },
        {
          title: this.$t('attendanceDetail.total4'),
          slot: 'total_hour4',
          width: 100,
          align: 'center',
        },
        {
          title: this.$t('attendanceDetail.attendance'),
          key: 'att_status',
          width: 130,
          align: 'center',
        },
        {
          title: this.$t('attendanceDetail.presentStatus'),
          key: 'att_present_status',
          width: 130,
          align: 'center',
        },
      ]
    },
  },
  methods: {
    ...mapActions('report/attendanceDetail', [
      'getOrgChartStructure',
      'getEmployee',
      'clearEmployee',
    ]),
    async fetchResource() {
      this.loading = true
      await this.getOrgChartStructure()
      this.loading = false
    },
    async getEmployeeByOrg() {
      this.EmployeeLoading = true
      await this.clearEmployee()
      await this.getEmployee({
        id: this.org_chart_structure_id ? this.org_chart_structure_id : '',
      })
      this.EmployeeLoading = false
    },
    onChangeDateFrom(data) {
      this.dateFrom = data
    },
    onChangeDateTo(data) {
      this.dateTo = data
    },
    async fetchData(attributes) {
      this.loading = true
      this.errors = new Errors()
      this.$store
        .dispatch('report/attendanceDetail/fetch', {
          date_from: this.dateFrom,
          date_to: this.dateTo,
          org_chart_structure_id: this.org_chart_structure_id,
          employee_id: this.employee_id,
          ...attributes,
        })
        .then(() => {
          this.loading = false
        })
        .catch((error) => {
          this.loading = false
          this.errors = new Errors(error.errors)
          this.notice({ type: 'error', text: error.message })
        })
    },
    exportExcel() {
      this.exporting = true
      this.errors = new Errors()
      this.$store
        .dispatch('report/attendanceDetail/attendanceDetailExcel', {
          date_from: this.dateFrom,
          date_to: this.dateTo,
          org_chart_structure_id: this.org_chart_structure_id,
          employee_id: this.employee_id,
        })
        .then((response) => {
          let filename = trim(
            response.headers['content-disposition']
              .substring(
                response.headers['content-disposition'].indexOf('filename')
              )
              .replace('filename=', ''),
            '"'
          )
          FileDownload(response.data, filename)
        })
        .catch((err) => {
          let error = JSON.parse(
            String.fromCharCode.apply(null, new Uint8Array(err))
          )
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => (this.exporting = false))
    },
    notice(not) {
      this.$Notice[not.type]({
        title: 'DAILY ATTENDANCES',
        desc: not.text,
      })
    },
  },
  watch: {
    org_chart_structure_id: function () {
      this.getEmployeeByOrg()
    },
  },
  beforeRouteLeave(to, from, next) {
    this.$store.commit('report/attendanceDetail/RESET_STATE')
    next()
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      vm.fetchResource()
    })
  },
}
</script>
